import "@finn-no/favorite-heart-component/dist/style.css";
import { i18n } from "@lingui/core";
import IconChevronLeft24 from "@warp-ds/icons/react/chevron-left-24";
import IconChevronRight24 from "@warp-ds/icons/react/chevron-right-24";
import React, { useCallback, useMemo, useState } from "react";
import { Recommendation } from "./components/Recommendation.js";
import { useTrackVisibility } from "./hooks/trackVisibility.js";
import { useRecommendations } from "./providers/RecommendationsProvider.js";

export const Pagination = () => {
	const [showBackButton, setShowBackButton] = useState(false);
	const [showForwardButton, setShowForwardButton] = useState(true);
	const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);

	const { items } = useRecommendations();
	const filteredRecommendations = useMemo(() => items.filter(({ price }) => !!price) || [], [items]);

	useTrackVisibility(filteredRecommendations);

	const isTouchDevice = window?.matchMedia("(pointer: coarse)").matches;

	const handleScroll = useCallback((container: HTMLDivElement) => {
		const isNearStart = container.scrollLeft > 25;
		const isNearEnd = container.scrollWidth - container.offsetWidth - container.scrollLeft > 25;

		setShowBackButton(isNearStart);
		setShowForwardButton(isNearEnd);
	}, []);

	const scrollContainerRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (node) {
				setScrollContainer(node); // Store the node in state for later use
				node.addEventListener("scroll", () => handleScroll(node));
				handleScroll(node); // Initialize button states on mount
			}
		},
		[handleScroll],
	);

	const scrollList = (direction: "forward" | "backward") => {
		if (scrollContainer) {
			const scrollAmount = scrollContainer.offsetWidth + 12;
			const currentScroll = scrollContainer.scrollLeft;
			const newScrollPosition = direction === "forward" ? currentScroll + scrollAmount : currentScroll - scrollAmount;

			scrollContainer.scrollTo({ left: newScrollPosition, behavior: "smooth" });
		}
	};

	if (!filteredRecommendations?.length) {
		return null;
	}

	return (
		<section className="flex flex-col gap-16 relative max-w-[1010]">
			<h3>
				{i18n.t({
					id: "mobility-recommendations-title",
					message: "Recommendations for you",
				})}
			</h3>
			<div
				className="flex gap-16 overflow-x-auto overflow-y-hidden rounded-4 -mr-16 md:mr-0 pl-2"
				ref={scrollContainerRef}
				style={{ scrollbarWidth: "none" }}
			>
				{filteredRecommendations.map((recommendation) => {
					return <Recommendation key={recommendation.itemId} recommendation={recommendation} />;
				})}
			</div>
			{!isTouchDevice && showBackButton && (
				<button
					className="absolute -left-24 top-[160] transform -translate-y-1/2 s-bg-inverted rounded-full w-48 h-48 flex items-center justify-center shadow-m z-10"
					onClick={() => scrollList("backward")}
				>
					<IconChevronLeft24 className="s-icon-inverted" />
				</button>
			)}
			{!isTouchDevice && showForwardButton && filteredRecommendations.length > 3 && (
				<button
					className="absolute -right-24 top-[160] transform -translate-y-1/2 s-bg-inverted rounded-full w-48 h-48 flex items-center justify-center shadow-m z-10"
					onClick={() => scrollList("forward")}
				>
					<IconChevronRight24 className="s-icon-inverted" />
				</button>
			)}
		</section>
	);
};
